import parser from 'ua-parser-js'

import { INCLUDE_NON_LIVE_PRODUCTS } from '@src/config/constants'
import { EMPTY_OBJECT } from '@src/quince-site-common/src/constants/app'
import QuinceApiError from '@src/quince-site-common/src/domains/quince-api-error'
import wishlistStateManager from '@src/quince-site-common/src/redux/globalStateManager/WishlistStateManager'
import { removeCartIdFromLocalStorage } from '@src/quince-site-common/src/services/cartService/cart.service.helper'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { captureException } from '../components/Sentry/utils'
import * as theme from '../styles/theme'
import { logout } from './auth'
import { filterUnisexProductImages, filterUnisexSubcollection } from './build'
import { ANALYTIC_COOKIES, IDME_USER_DETAILS } from './constants'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { getFormattedSlug } from '@src/pages/[...slug]/components/collection/utils'
import { getLocalisationHeaders } from '@src/utils/general.utils'
import { signOut, getIsLoggedInAsync, getAuthTokens } from '@lastbrand/fe-common/universal-auth'
import { SESSION_EXPIRED_KEY } from '../templates/Auth/utils'
import { WISHLIST_LOCAL_STORAGE_KEY } from '@src/quince-site-common/src/services/wishlistService'
import { getStorage } from '@src/utils/enhancedStorage'
import { removeCheckoutIdFromLocalStorage } from '@src/quince-site-common/src/services/cartCheckoutService/checkout.service.helper'
import { RECENTLY_VIEWED_PRODUCTS_KEY } from '@src/quince-site-common/src/contexts/recently-viewed-products/recently-viewed-products.constants'
import { trackEvent } from '@src/telemetry/track'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import { FEATURES } from '@src/quince-site-common/src/constants/template-info'

export * from './auth'
export * from './localStorage'
export * from './validation'

export const MADE_TO_ORDER_CONTENTFUL_TAG = 'MADE_TO_ORDER'

// @i18n-ignore-start
export const ContentSize = {
  EXTRA_SMALL: 'Extra Small',
  SMALL: 'Small',
  MEDIUM: 'Medium',
  LARGE: 'Large',
}
// @i18n-ignore-end

export const getLinkPath = link => {
  if (!link) return '/'
  if (link.entry?.slug) return `/${getFormattedSlug(link.entry.slug)}`
  if (link.slug) return `/${getFormattedSlug(link.slug)}`
  if (link.url) return link.url
  return '/'
}

export const getLinkHref = link => {
  if (!link) return undefined
  if (link.url) return link.url
  return undefined
}

export const getPathName = (...slugs) => {
  const prependSlashIfNotEmpty = slug => {
    return slug ? `/${slug.toString().split(' ').join('-')?.toLowerCase()}` : '/'
  }

  return slugs.map(prependSlashIfNotEmpty).join('')
}

export const set = (obj, path, value) => {
  // Regex explained: https://regexr.com/58j0k
  const pathArray = Array.isArray(path) ? path : path.match(/([^[.\]])+/g)

  pathArray?.reduce((acc, key, i) => {
    if (acc[key] === undefined) acc[key] = {}
    if (i === pathArray?.length - 1) acc[key] = value
    return acc[key]
  }, obj)
}
export const getImageFluidForContentfulNewBuildImage = image => {
  const url = image.file.url
  const aspectRatio = image.file.details.image.width / image.file.details.image.height
  const reverseAspectRatio = 1 / aspectRatio
  const srcSetWidths = [360, 720, 1440, 1600]
  const srcSetString = srcSetWidths
    .map(w => `${url}?w=${Math.ceil(w)}&h=${Math.ceil(w * reverseAspectRatio)}&q=50 ${w}w`)
    .join(',\n')
  const srcSetWebpString = srcSetWidths
    .map(w => `${url}?w=${Math.ceil(w)}&h=${Math.ceil(w * reverseAspectRatio)}&q=50&fm=webp ${w}w`)
    .join(',\n')
  return {
    ...image,
    fluid: {
      aspectRatio,
      sizes: `(max-width: 1440px) 100vw, 1440px`,
      src: `${url}?w=1440&q=50`,
      srcSet: srcSetString,
      srcSetWebp: srcSetWebpString,
      srcWebp: `${url}?w=1440&q=50&fm=webp`,
    },
    fixed: {
      height: image.file.details.image.height,
      src: `${url}?w=${image.file.details.image.width}&h=${image.file.details.image.height}&q=50&fit=fill`,
      srcSet: `${url}?w=${image.file.details.image.width}&h=${image.file.details.image.height}&q=50&fit=fill 1x`,
      width: image.file.details.image.width,
    },
  }
}

export const getHumanReadableTitleFromSlug = slug => {
  const words = slug.split('/')
  const humanReadableWords = []
  words.forEach(word => {
    const subWords = word.split('-')
    if (subWords.length > 1) {
      humanReadableWords.push(subWords.map(subWord => capitalizeFirstLetter(subWord)).join(' '))
    } else {
      humanReadableWords.push(capitalizeFirstLetter(word))
    }
  })
  return humanReadableWords.reverse().join(' - ')
}

export const getPathParams = (params, slug = '') => {
  const urlParams = new URLSearchParams()
  for (const key in params) {
    if (params[key]) {
      urlParams.append(key, params[key])
    }
  }
  if ((slug || '').search(/\bmen\b/) >= 0) urlParams.append('gender', 'men')
  if ((slug || '').search(/\bwomen\b/) >= 0) urlParams.append('gender', 'women')
  return `?${urlParams.toString()}`
}

export const decodeIdFromGraphQLResponse = (idResponse = '') => idResponse.toString().split('/').pop()

export const encodeIdBase64 = id => btoa(id)

export const getHyphenatedColorName = str => str.split(' ').join('-')?.toLowerCase()

export const getFullNameFromHyphenatedName = color =>
  color.charAt(0).toUpperCase() + color.slice(1).replace(/-([a-z])/g, g => ` ${g[1].toUpperCase()}`)

export const getValidColorName = str => str.replace(/\b\w/g, l => l.toUpperCase()).replace(/\s+$/g, '')

// Check for sizes other than xs s m l xl
export const showSizesWithPrice = sizes => {
  return sizes.length > 0 && sizes[0].length > 2 && sizes[0] !== 'One Size'
}

export const eraseCookie = name => {
  setCookie(name, undefined, -1)
}

/**
 * @deprecated Use EnhancedCookie utility instead:
 * ```typescript
 * import { setCookieLocalized } from '@src/utils/enhancedCookie'
 * ```
 */
// Expiry is optional, if not set then cookie stays active till year 2035
export const setCookie = (name, value, expiryMinutes, sameDomain) => {
  if (!isBrowser()) return
  let expires = ''
  if (!expiryMinutes) {
    // @i18n-ignore
    expires = 'Fri, 18 May 2035 12:00:00 UTC'
  }
  if (expiryMinutes) {
    const date = new Date()
    date.setTime(date.getTime() + expiryMinutes * 60 * 1000)
    expires = date.toUTCString()
  }
  if (sameDomain) {
    document.cookie = `${name}=${value}; expires=${expires}; path=/;`
  } else {
    document.cookie = `${name}=${value}; expires=${expires}; path=/; domain=${getCookieDomain()}`
  }
}

/**
 * @deprecated Use EnhancedCookie utility instead:
 * ```typescript
 * import { removeCookieLocalized } from '@src/utils/enhancedCookie'
 * ```
 */
export function deleteCookie(name) {
  if (getCookie(name)) {
    document.cookie = `${name}=;path=/;domain=${getCookieDomain()};expires=Thu, 01 Jan 1970 00:00:01 GMT`
  }
}

const getCookieDomain = () => {
  const hostName = window.location.hostname
  return hostName.split('.').slice(-2).join('.')
}

/**
 * @deprecated Use EnhancedCookie utility instead:
 * ```typescript
 * import { getCookieLocalized } from '@src/utils/enhancedCookie'
 * ```
 */
export const getCookie = cname => {
  if (!isBrowser()) return undefined
  // @i18n-ignore
  const name = `${cname}=`
  try {
    const decodedCookie = document.cookie
    const cookiesArray = decodedCookie.split(';')
    for (let cookie of cookiesArray) {
      cookie = cookie.trimLeft()
      if (cookie.startsWith(name)) {
        return cookie.substring(name.length, cookie.length)
      }
    }
  } catch (error) {
    captureException(error)
  }
  return undefined
}

export const getHostNameFromURL = url => {
  const match = url.match(/:\/\/(www[0-9]?\.)?(.[^/:]+)/i)
  if (match != null && match.length > 2 && typeof match[2] === 'string' && match[2].length > 0) {
    return match[2]
  }
  return null
}

export const extractDomainFromURL = url => {
  const hostName = getHostNameFromURL(url)
  let domain = hostName
  if (hostName != null) {
    const parts = hostName.split('.').reverse()
    if (parts != null && parts.length > 1) {
      domain = `${parts[1]}.${parts[0]}`
      if (hostName.toLowerCase().includes('.co.uk') && parts.length > 2) {
        domain = `${parts[2]}.${domain}`
      }
    }
    domain = domain.split('.')[0]
  }
  return domain
}

// Extracts parameter value for input key present in the current page path.
export const extractQueryParamFromPageLocation = key => {
  return decodeURIComponent(
    window.location.search.replace(
      // eslint-disable-next-line no-useless-escape
      new RegExp(`^(?:.*[&\\?]${encodeURIComponent(key).replace(/[\.\+\*]/g, '\\$&')}(?:\\=([^&]*))?)?.*$`, 'i'),
      '$1',
    ),
  )
}

export const getUserDevice = () => {
  const width = window.innerWidth
  let screenType
  if (width <= 520) {
    screenType = 'mobile'
  } else if (width <= 820) {
    screenType = 'tablet'
  } else {
    screenType = 'desktop'
  }
  return screenType
}

export const getUserDeviceCategory = () => {
  if (!navigator.userAgent) {
    return undefined
  }
  let deviceCategory = 'Unknown'
  const ua = {
    'Generic Linux': /Linux/i,
    Android: /Android/i,
    BlackBerry: /BlackBerry/i,
    Bluebird: /EF500/i,
    'Chrome OS': /CrOS/i,
    Datalogic: /DL-AXIS/i,
    Honeywell: /CT50/i,
    iPad: /iPad/i,
    iPhone: /iPhone/i,
    iPod: /iPod/i,
    macOS: /Macintosh/i,
    Windows: /IEMobile|Windows/i,
    Zebra: /TC70|TC55/i,
  }
  Object.keys(ua).forEach(v => navigator.userAgent.match(ua[v]) && (deviceCategory = v))
  return deviceCategory
}

export const extractLineItemFromGraphEdge = lineItemEdge => {
  const { id, variant } = lineItemEdge.node
  return {
    id,
    sku: variant.sku,
    price: variant.priceV2.amount,
    title: lineItemEdge.node.title,
    quantity: lineItemEdge.node.quantity,
    productId: decodeIdFromGraphQLResponse(variant.product.id),
    variantId: decodeIdFromGraphQLResponse(variant.id),
    variantTitle: variant.title,
  }
}

export const capitalizeFirstLetter = str => `${str[0]?.toUpperCase()}${str.slice(1)}`

export const capitalizeWord = str => {
  return str.replace(/(^([a-zA-Z]))|([ -][a-zA-Z])/g, function (firstLetter) {
    return firstLetter.toUpperCase()
  })
}

export const capitalize = str => {
  return str.charAt(0).toUpperCase() + str.slice(1)?.toLowerCase()
}
export const capitalizeAllWords = str => {
  return str.toUpperCase()
}

export const isValidEmail = email => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(String(email).toLowerCase())
}

export const isValidUSNumber = number => {
  return /^\(?(\d{3})\)?[- ]?(\d{3})[- ]?(\d{4})$/.test(number)
}

export const isFutureDate = dateStr => {
  const d = new Date(dateStr)
  const now = new Date()
  return d > now
}

export const getLongFormDate = dateStr => {
  const options = {
    // @i18n-ignore
    weekday: 'long',
    // @i18n-ignore
    month: 'long',
    // @i18n-ignore
    day: 'numeric',
  }
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-US', options)
}

export const imageSources = (mobile, desktop) => [
  mobile.fluid,
  desktop
    ? {
        ...desktop.fluid,
        media: `(min-width: ${theme.breakpoints.desktopSmall}px)`,
      }
    : {},
]

export const getNearestColorShades = hexcodes => {
  const nearestColors = []
  const iterColors = []
  const windowGlobal = typeof window !== 'undefined' && window

  if (windowGlobal) {
    hexcodes.forEach(hexcode => {
      const shade = window.ntc.name(hexcode)[3]
      if (!iterColors.includes(shade)) {
        nearestColors.push({ name: window.ntc.name(hexcode)[3] })
        iterColors.push(shade)
      }
    })
  }
  return nearestColors
}

// This information is used during on-the-fly color filters
// export const enrichProductsWithNearestColors = products => {
//   return (products || []).map(product => {
//     const productColors = [
//       ...new Set(
//         product.colors
//           .filter(color => color.hexcode != null)
//           .map(color => color.hexcode),
//       ),
//     ]
//     const nearestColors = getNearestColorShades(productColors)
//     return {
//       ...product,
//       attributes: product.attributes
//         ? product.attributes.concat(nearestColors)
//         : nearestColors,
//     }
//   })
// }

export const getMetaProducts = (categorySlug, collectionSlug, subcollectionSlug, subcollection) => {
  let newMetaProducts = subcollection.metaProducts
  if (
    [categorySlug, collectionSlug, subcollectionSlug].includes('women') ||
    [categorySlug, collectionSlug, subcollectionSlug].includes('men')
  ) {
    newMetaProducts = (subcollection.metaProducts || []).map(metaItem => {
      const newMetaProduct = filterUnisexProductImages(
        metaItem.product,
        categorySlug,
        collectionSlug,
        subcollectionSlug,
      )
      return { ...metaItem, product: newMetaProduct }
    })
  }
  return newMetaProducts
  // return enrichProductsWithNearestColors(newMetaProducts).filter(
  //   metaProduct => !!metaProduct,
  // )
}

export const getProducts = (categorySlug, collectionSlug, subcollectionSlug, subcollection) => {
  let filteredSubcollection = subcollection
  if (
    [categorySlug, collectionSlug, subcollectionSlug].includes('women') ||
    [categorySlug, collectionSlug, subcollectionSlug].includes('men')
  ) {
    filteredSubcollection = filterUnisexSubcollection(subcollection, categorySlug, collectionSlug, subcollectionSlug)
  }
  return filteredSubcollection.products || []
  // return enrichProductsWithNearestColors(filteredSubcollection.products).filter(
  //   product => !!product,
  // )
}

export const getDimensions = ele => {
  const { height } = ele.getBoundingClientRect()
  const { offsetTop } = ele
  const offsetBottom = offsetTop + height

  return {
    height,
    offsetTop,
    offsetBottom,
  }
}

export const getImageFluid = (mobileDevice, image) => {
  return {
    ...image.fluid,
    // @i18n-ignore
    sizes: '(max-width: 768px) 72rem, 72rem',
  }
}

export const uniqueElementsBy = (arr, fn) =>
  arr.reduce((acc, v) => {
    if (!acc.some(x => fn(v, x))) acc.push(v)
    return acc
  }, [])

export const getPickedForYouCollectionDisplayName = (userInfo, defaultValue) => {
  if (userInfo?.name) {
    const firstName = userInfo.name.trim()
    if (firstName.length > 1 && firstName.length < 11) {
      return `${firstName}'s Quince`
    }
  }
  return defaultValue
}

export const checkIfLiveProduct = product => product.live === true || INCLUDE_NON_LIVE_PRODUCTS === 'true'

export const debounce = (func, delay) => {
  let debounceTimer
  return (...args) => {
    const context = this
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => func.call(context, ...args), delay)
  }
}

export const wait = ms => {
  return new Promise(resolve => setTimeout(resolve, ms))
}

const getMyAccountDisplayName = (userInfo, defaultValue) => {
  if (userInfo?.name) {
    const firstName = userInfo.name.trim()
    if (firstName.length > 1 && firstName.length < 13) {
      return `Hi, ${firstName}`
    }
  }
  return defaultValue
}

export function getOverrideMenu(menu, userInfo) {
  return menu.map(m => {
    if (m?.link && getLinkPath(m.link) === '/collections/picked-for-you') {
      m.link.overrideText = getPickedForYouCollectionDisplayName(userInfo, undefined)
    }
    if (m?.link && getLinkPath(m.link) === '/log-in') {
      m.link.overrideText = getMyAccountDisplayName(userInfo, undefined)
    }
    return m
  })
}

export const getStyledPrice = price => {
  const priceStr = parseFloat(price).toFixed(2)
  const splittedPrice = priceStr.split('.')
  if (splittedPrice[1] === undefined) return splittedPrice[0]
  return splittedPrice[0] + splittedPrice[1].sup()
}

export function getMetaTagMap(shippingTime, madeToOrderText, shouldShowMtoTextForVariant) {
  let mtoText = ''
  if (shouldShowMtoTextForVariant) {
    mtoText = madeToOrderText
      ? madeToOrderText.json.content[0].content[0].value
      : shippingTime || '"Made to Order" items will arrive to you in 15-20 business days'
  }
  const META_TAGS_MAP = {
    [MADE_TO_ORDER_CONTENTFUL_TAG]: {
      // @i18n-ignore
      key: 'Made to Order',
      value: mtoText,
    },
  }
  return [META_TAGS_MAP[MADE_TO_ORDER_CONTENTFUL_TAG]]
}

export function getLineItemPropertiesFromProductMetaTags(
  metaTags,
  shippingInformation,
  madeToOrderText,
  shouldShowMtoTextForVariant,
) {
  if (!(metaTags || []).includes(MADE_TO_ORDER_CONTENTFUL_TAG) && !shippingInformation) return []
  return getMetaTagMap(shippingInformation, madeToOrderText, shouldShowMtoTextForVariant)
}

export const pick = (obj, arr) => arr.reduce((acc, curr) => (curr in obj && (acc[curr] = obj[curr]), acc), {})

export function checkForWordInURL(referrer, word) {
  return new RegExp(word, 'g').test(referrer)
}

export function executeSafely(fn = () => {}) {
  try {
    fn()
  } catch (e) {
    captureException(e)
  }
}

export function fallbackCopyTextToClipboard(text) {
  const textArea = document.createElement('textarea')
  textArea.value = text

  // Avoid scrolling to bottom
  textArea.style.top = '0'
  textArea.style.left = '0'
  // @i18n-ignore
  textArea.style.position = 'fixed'

  document.body.appendChild(textArea)
  textArea.focus()
  textArea.select()

  try {
    const successful = document.execCommand('copy')
    // @i18n-ignore
    const msg = successful ? 'successful' : 'unsuccessful'
    console.log(`Fallback: Copying text command was ${msg}`)
  } catch (err) {
    console.error('Fallback: Oops, unable to copy', err)
  }

  document.body.removeChild(textArea)
}

export const groupBy = (arr, fn) =>
  arr.map(typeof fn === 'function' ? fn : val => val[fn]).reduce((acc, val, i) => {
    acc[val] = (acc[val] || []).concat(arr[i])
    return acc
  }, {})

export function isUtmAttentiveText() {
  return extractQueryParamFromPageLocation('utm_source') === 'attentive'
}

export function addWorkDays(startDate, days) {
  if (isNaN(days)) {
    console.log('Value provided for "days" was not a number')
    return
  }
  if (!(startDate instanceof Date)) {
    console.log('Value provided for "startDate" was not a Date object')
    return
  }
  // Get the day of the week as a number (0 = Sunday, 1 = Monday, .... 6 = Saturday)
  const dow = startDate.getDay()
  let daysToAdd = parseInt(days)
  // If the current day is Sunday add one day
  if (dow === 0) daysToAdd++
  // If the start date plus the additional days falls on or after the closest Saturday calculate weekends
  if (dow + daysToAdd >= 6) {
    // Subtract days in current working week from work days
    const remainingWorkDays = daysToAdd - (5 - dow)
    // Add current working week's weekend
    daysToAdd += 2
    if (remainingWorkDays > 5) {
      // Add two days for each working week by calculating how many weeks are included
      daysToAdd += 2 * Math.floor(remainingWorkDays / 5)
      // Exclude final weekend if remainingWorkDays resolves to an exact number of weeks
      if (remainingWorkDays % 5 === 0) daysToAdd -= 2
    }
  }
  startDate.setDate(startDate.getDate() + daysToAdd)
  return startDate
}

export const getSlugFromEnd = path =>
  typeof path === 'string' ? ((path.split('/') || []).slice(-1) || [])[0] : undefined

export const scrollToSection = (section, collectionListRef, offset = 0, headerHeight = 0, behavior = 'smooth') => {
  const collectionListRefElement = collectionListRef[section]
  if (!collectionListRefElement) return
  window.scrollTo({
    top: collectionListRefElement.current.getBoundingClientRect().top - offset + window.scrollY - headerHeight,
    behavior,
  })
}
export const getUserBrowser = () => {
  const ua = parser(navigator.userAgent)
  return ua.browser.name
}

export const getUserTrafficSource = () => {
  let source = getCookie(ANALYTIC_COOKIES.UTM_SOURCE)

  switch (source) {
    case 'fb':
      // @i18n-ignore
      source = 'facebook'
      break

    case 'ig':
      // @i18n-ignore
      source = 'instagram'
      break

    case 'msg':
      // @i18n-ignore
      source = 'messenger'
      break

    case 'an':
      source = 'audience_network'
      break
  }

  return source
}

export function getOvpdCustomAttribute() {
  return []
}

export function getRequiredVariant(product, selectedColor, selectedSize) {
  let requiredVariant = null
  ;(product?.variants?.edges ?? product?.variants ?? []).forEach(edge => {
    if (
      edge.node.selectedOptions.filter(option => option.name === 'Color' && option.value === selectedColor).length > 0
    ) {
      if (
        edge.node.selectedOptions.filter(option => option.name === 'Size' && option.value === selectedSize).length > 0
      ) {
        if (edge.node.selectedOptions.filter(option => option.value === 'Group').length === 0) {
          requiredVariant = edge.node
        }
      }
    }
  })
  return requiredVariant
}

export function getProductImagesOnSizeAndColor(productImages, selectedSize, selectedColor) {
  return productImages.find(({ color, size }) => {
    if (size && selectedSize) {
      return size === selectedSize && color.name === selectedColor
    }
    return color.name === selectedColor
  })
}

export const decodeAddressIdFromGraphQLResponse = (idResponse = '') =>
  idResponse.toString().split('/').pop().split('?')[0]

export function isGiftCard(productId) {
  return productId === '2206467817583'
}

export function getEagerLoadCarouselClassName(image, suffix) {
  return `eager_load_carousel_image__${image?.color.toString().split(' ').join('-')?.toLowerCase()}__${suffix}`
}

export function getFirstNProductColorImages(productImages, noOfImages) {
  // this will ensure we don't count multiple images of same color with different size
  // e.g. organic crew socks
  const visitedColorBucket = {}
  return (productImages || []).reduce((imgAcc, colorImg) => {
    ;(colorImg?.images ?? []).slice(0, noOfImages).forEach(imgObj => {
      // this will work since we're only considering first image of the color
      if (!visitedColorBucket[`${colorImg?.color?.name}-${colorImg?.bucket}`]) {
        visitedColorBucket[`${colorImg?.color?.name}-${colorImg?.bucket}`] = true
        imgAcc = [...imgAcc, { ...imgObj, color: colorImg?.color?.name, bucket: colorImg?.bucket }]
      }
    })
    return imgAcc
  }, [])
}

export function isEligibleProductForCarouselImageExperiment(contentfulProduct) {
  if (!contentfulProduct?.sp?.product_id) return false
  return ['2067473662063'].includes(decodeIdFromGraphQLResponse(contentfulProduct?.sp?.product_id))
}

export function getNewLineItemsWithTransformedSrc(lineItems) {
  // TODO: Fix this
  // let newLineItems = lineItems?.edges ?? []

  // if (isAssignedExperimentBucket('product_images', 'show')) {
  //   newLineItems = (lineItems?.edges ?? []).map(lineItem => {
  //     const variantId = decodeIdFromGraphQLResponse(lineItem?.node?.variant?.id)
  //     if (variantToImageSrc[variantId]) {
  //       return {
  //         ...lineItem,
  //         node: {
  //           ...lineItem.node,
  //           variant: {
  //             ...lineItem?.node?.variant,
  //             image: {
  //               transformedSrc: variantToImageSrc[variantId],
  //             },
  //           },
  //         },
  //       }
  //     }
  //     return lineItem
  //   })
  // }
  return lineItems?.edges ?? []
}

/**
 * More accurately check the type of a JavaScript object
 * @param  {Object} obj The object
 * @return {String}     The object type
 */
export const deepEqual = (obj1, obj2) => {
  function getType(obj) {
    return Object.prototype.toString.call(obj).slice(8, -1).toLowerCase()
  }

  function areArraysEqual() {
    // Check length
    if (obj1.length !== obj2.length) return false

    // Check each item in the array
    for (let i = 0; i < obj1.length; i++) {
      if (!deepEqual(obj1[i], obj2[i])) return false
    }

    // If no errors, return true
    return true
  }

  function areObjectsEqual() {
    if (Object.keys(obj1).length !== Object.keys(obj2).length) return false

    // Check each item in the object
    for (const key in obj1) {
      if (Object.prototype.hasOwnProperty.call(obj1, key)) {
        if (!deepEqual(obj1[key], obj2[key])) return false
      }
    }

    // If no errors, return true
    return true
  }

  function areFunctionsEqual() {
    return obj1.toString() === obj2.toString()
  }

  function arePrimativesEqual() {
    return obj1 === obj2
  }

  // Get the object type
  const type = getType(obj1)

  // If the two items are not the same type, return false
  if (type !== getType(obj2)) return false

  // Compare based on type
  if (type === 'array') return areArraysEqual()
  if (type === 'object') return areObjectsEqual()
  if (type === 'function') return areFunctionsEqual()
  return arePrimativesEqual()
}

const vwToPx = value => {
  const viewWidth =
    window.innerWidth || document.documentElement.clientWidth || document.getElementsByTagName('body')[0].clientWidth
  return (viewWidth * value) / 100
}

export const scrollTo = ref => {
  const menuHeight = vwToPx(5.486)
  const bodyRect = document.body.getBoundingClientRect()
  const elRect = ref?.current?.getBoundingClientRect()
  const offset = elRect?.top - bodyRect.top - menuHeight

  window.scrollTo({
    top: offset,
    // @i18n-ignore
    behavior: 'smooth',
  })
}

// TODO: handle this at global level with aws amplify federation
// https://docs.amplify.aws/lib/auth/advanced/q/platform/js/#retrieve-jwt-tokens
export async function getUserSession() {
  try {
    const isLoggedIn = await getIsLoggedInAsync()
    if (isLoggedIn) {
      const tokens = await getAuthTokens()
      const { idToken, accessToken } = tokens || {}
      if (tokens) {
        // Extract session info from universal auth user
        const userSession = {
          idToken: {
            jwtToken: idToken,
          },
          accessToken: {
            jwtToken: accessToken,
          },
        }

        return userSession
      }
    }
    return null
  } catch (error) {
    if (error !== 'No current user' && error?.name !== 'NotAuthorizedException') {
      const quinceError = new QuinceApiError({
        error: error,
        // @i18n-ignore
        message: 'Error getting user session',
        // @i18n-ignore
        apiName: 'get user session : cognito',
      })
      captureException(quinceError, {
        // @i18n-ignore
        capturedFrom: 'getUserSession in utils',
      })
    }
    if (AuthUtil.isLoggedIn()) {
      const queryParams = new URLSearchParams({ status: SESSION_EXPIRED_KEY })
      // redirect to `/log-in?${queryParams?.toString()}
      signOut().then(() => {
        // Clear user session cache on signout
        if (typeof window !== 'undefined' && window.clearUserSessionCache) {
          window.clearUserSessionCache()
        }
        deleteCookie('refresh_token')
        // redirect
        logout(() => {
          window.location.href = `/log-in?${queryParams?.toString()}`
        })
      })
    }
  }
}

export async function getOptionsWithBearerToken(existingOptions = EMPTY_OBJECT, originUrl = '') {
  const userSession = await getUserSession()
  const options = { ...existingOptions }
  set(options, 'headers.Authorization', `Bearer ${userSession?.idToken?.jwtToken}`)
  const localisationHeaders = getLocalisationHeaders(originUrl)
  return { ...options, ...localisationHeaders }
}

function removeWishlistFromLocalStorageOnLogOut() {
  getStorage().removeItem(WISHLIST_LOCAL_STORAGE_KEY, { isGlobal: false, removeAllLocalized: true })
  wishlistStateManager.setWishlistTotalItemsCount(0)
}

function removeCartIdFromLocalStorageOnLogOut() {
  removeCartIdFromLocalStorage()
  removeCheckoutIdFromLocalStorage()
}

function removeRecentlyViewedProductsFromLocalStorageOnLogOut() {
  getStorage().removeItem(RECENTLY_VIEWED_PRODUCTS_KEY)
}

// function removeCustomerIdFromCookieOnLogOut() {
//   deleteCookie(ANALYTIC_COOKIES.LB_CUSTOMER_ID)
// }

export async function logoutUser(
  navigateToHomeOnLogout = true,
  navigateToLoginOnLogout = false,
  refreshHref = false,
  skipQueryParam = false,
) {
  // Clear user session cache before logout
  if (typeof window !== 'undefined' && window.clearUserSessionCache) {
    window.clearUserSessionCache()
  }

  signOut()
    .then(
      logout(() => {
        trackEvent(TELEMETRY_EVENT_NAMES.FORM_SUBMIT, {
          trackInternal: true,
          // @i18n-ignore
          category: 'login',
          action: 'account_logout',
          context: {
            lb_user_id: getlbUserId(),
            navigateToHomeOnLogout: navigateToHomeOnLogout,
            navigateToLoginOnLogout: navigateToLoginOnLogout,
            refreshHref: refreshHref,
          },
        })

        removeWishlistFromLocalStorageOnLogOut()
        removeCartIdFromLocalStorageOnLogOut()
        removeRecentlyViewedProductsFromLocalStorageOnLogOut()
        if (navigateToHomeOnLogout) {
          window.location.href = '/'
        } else if (navigateToLoginOnLogout) {
          let queryParams = skipQueryParam ? '' : `?status=${SESSION_EXPIRED_KEY}`
          window.location.href = `/log-in${queryParams}`
        } else if (refreshHref) {
          window.location.href = `${location.pathname}${location.search}`
        }
      }),
    )
    .catch(err => {
      console.error('Error during logout:', err)
      trackEvent(TELEMETRY_EVENT_NAMES.FORM_SUBMIT, {
        trackInternal: true,
        // @i18n-ignore
        category: 'login',
        action: 'account_logout_error',
        context: {
          lb_user_id: getlbUserId(),
          error: err.message,
        },
      })
    })
}

// NOTE: hack to make user session available in ecomm-packages as well.
if (typeof window !== 'undefined') {
  window.getUserSession = getUserSession
  window.logoutUser = logoutUser
}

// @i18n-ignore
export const BLOCK_RETURNS_FOR_PROMO_CODE = ['PERCALEFLAT40', 'SILKFLASHSALE', 'PILLOWFLASHSALE', 'SILKSETFLASHSALE']

export function isInViewport(el) {
  const rect = el.getBoundingClientRect()

  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom - 300 <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  )
}

export const isStaging = () => {
  return INCLUDE_NON_LIVE_PRODUCTS === 'true'
}

export const getUserEmailAssociatedWithIdmeDiscount = () => {
  const couponCodeString = window.localStorage.getItem(IDME_USER_DETAILS)
  if (couponCodeString) {
    const idmeUser = JSON.parse(couponCodeString).idMeUser
    return idmeUser?.email
  }
  return null
}

export const usRegex = /^\d{5}(?:-\d{4})?$/
export const canadianRegex = /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/

// same as logout function, testing this function
// deprecate previous fn and use this in future
export async function logoutUserFn(
  navigateToHomeOnLogout = true,
  navigateToLoginOnLogout = false,
  refreshHref = false,
  skipQueryParam = false,
) {
  // Clear user session cache before logout
  if (typeof window !== 'undefined' && window.clearUserSessionCache) {
    window.clearUserSessionCache()
  }
  signOut()
    .then(() => {
      logout(() => {
        trackEvent(TELEMETRY_EVENT_NAMES.LOGOUT, {
          trackInternal: true,
          // @i18n-ignore
          category: 'login',
          action: 'account_logout',
          context: {
            lb_user_id: getlbUserId(),
            navigateToHomeOnLogout: navigateToHomeOnLogout,
            navigateToLoginOnLogout: navigateToLoginOnLogout,
            refreshHref: refreshHref,
            referrer: typeof window !== 'undefined' && window.location.href,
            feature: FEATURES.FIND_MY_ORDER,
          },
        })

        removeWishlistFromLocalStorageOnLogOut()
        removeCartIdFromLocalStorageOnLogOut()
        removeRecentlyViewedProductsFromLocalStorageOnLogOut()
        if (navigateToHomeOnLogout) {
          window.location.href = '/'
        } else if (navigateToLoginOnLogout) {
          let queryParams = skipQueryParam ? '' : `?status=${SESSION_EXPIRED_KEY}`
          window.location.href = `/log-in${queryParams}`
        } else if (refreshHref) {
          window.location.href = `${location.pathname}${location.search}`
        }
      })
    })
    .catch(err => {
      console.error('Error during logout:', err)
      trackEvent(TELEMETRY_EVENT_NAMES.LOGOUT, {
        trackInternal: true,
        // @i18n-ignore
        category: 'login',
        action: 'account_logout_error',
        context: {
          lb_user_id: getlbUserId(),
          error: err.message,
          feature: FEATURES.FIND_MY_ORDER,
        },
      })
    })
}
